import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../lib/apiClient";

/**
 * Voice transcription for the book prompt.
 * - Browser SpeechRecognition: progressive enhancement (interim + final)
 * - Backend /api/book-lab/transcribe: production path when MediaRecorder is used
 * Never auto-submits. Preserves existing prompt text on errors.
 */
export function useVoiceTranscription({ value, onChange }) {
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const promptBefore = useRef("");
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAt = useRef(null);
  const streamRef = useRef(null);

  const clearTimer = () => {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const startTimer = () => {
    startedAt.current = Date.now();
    setElapsed(0);
    timerRef.current = window.setInterval(() => {
      const sec = Math.floor((Date.now() - startedAt.current) / 1000);
      setElapsed(sec);
      const mm = String(Math.floor(sec / 60)).padStart(2, "0");
      const ss = String(sec % 60).padStart(2, "0");
      setStatus(`Listening… ${mm}:${ss}`);
    }, 250);
  };

  const applySpoken = useCallback((spoken) => {
    const cleaned = (spoken || "").trim();
    const base = promptBefore.current;
    onChange([base, cleaned].filter(Boolean).join(base && cleaned ? " " : ""));
  }, [onChange]);

  const stopAll = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch (_) { /* ignore */ }
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch (_) { /* ignore */ }
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    clearTimer();
    setRecording(false);
  }, []);

  const startBrowser = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return false;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onstart = () => {
      setRecording(true);
      setError(null);
      setStatus("Listening… 00:00");
      startTimer();
    };
    recognition.onresult = (event) => {
      let spoken = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        spoken += event.results[i][0].transcript;
      }
      applySpoken(spoken);
    };
    recognition.onend = () => {
      setRecording(false);
      clearTimer();
      setStatus((promptBefore.current || value || "").trim() ? "Transcription added" : "");
    };
    recognition.onerror = (event) => {
      setRecording(false);
      clearTimer();
      if (event.error === "not-allowed") {
        setError("permission");
        setStatus("Microphone permission denied");
      } else {
        setError("failed");
        setStatus("Transcription failed — your prompt text was kept.");
      }
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      return true;
    } catch (_) {
      return false;
    }
  }, [applySpoken, value]);

  const startBackendRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("unsupported");
      setStatus("Voice transcription is not supported in this browser.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data?.size) chunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        clearTimer();
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size) {
          setStatus("No audio captured");
          return;
        }
        try {
          const form = new FormData();
          form.append("file", blob, "book-prompt.webm");
          const res = await api.upload("/api/book-lab/transcribe", form);
          applySpoken(res.text || res.transcript || "");
          setStatus("Transcription added");
          setError(null);
        } catch (e) {
          setError("failed");
          setStatus(e.message || "Transcription failed — your prompt text was kept.");
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setError(null);
      setStatus("Listening… 00:00");
      startTimer();
      return true;
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        setError("permission");
        setStatus("Microphone permission denied");
        return false;
      }
      setError("failed");
      setStatus("Could not start recording");
      return false;
    }
  }, [applySpoken]);

  const toggle = useCallback(async () => {
    if (recording) {
      stopAll();
      return;
    }
    promptBefore.current = (value || "").trim();
    // Prefer live browser recognition when available; else backend MediaRecorder path.
    if (!startBrowser()) {
      await startBackendRecording();
    }
  }, [recording, startBackendRecording, startBrowser, stopAll, value]);

  const clearTranscription = useCallback(() => {
    onChange(promptBefore.current || "");
    setStatus("");
    setError(null);
  }, [onChange]);

  useEffect(() => () => stopAll(), [stopAll]);

  return {
    recording,
    status,
    error,
    elapsed,
    toggle,
    clearTranscription,
    retry: toggle,
  };
}
